import { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import TriggerOutboundMessageForm from '../TriggerOutboundMessageForm';
import Select from 'react-select';
import { Controller } from 'react-hook-form';
import service from '../../service';
import { useAuth } from '../../contexts/auth';
import { useTagAndSegment } from '../../contexts/tagAndSegment';
import { useFlow } from '../../contexts/flow';
import { orderFlowStages } from '../../utils';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { useMessages } from '../../contexts/messages';
import { LEGACY_SENDER_FIELD_INBOX_TYPES, Trigger, TriggerActionType } from '../../types/triggers';
import { Tag } from 'src/types/tags';
import { FlowStage, FlowStageType } from 'src/types/flowStage';
import { MergeTag } from 'src/types/mergeTags';
import { Flow } from 'src/types/flow';
import { ValueComparisonCondition } from '../ValueComparisonCondition';
import { EMPTY_EDITOR_DESIGN } from '@Constants/html-templates';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';

const REGISTRATION_COMPLETION_ID = '4f5b1de3-8e8a-4f93-9bb0-c47c3a9d02f2';
const DEFAULT_DELAY_UNITS = [
  'Minutes',
  'Hours',
  'Days',
  'Weeks',
  'Months',
  'Years',
];
const DEFAULT_TRIGGER_TYPES = ['Complete', 'Comparison', 'ValueComparison'];
const DEFAULT_OPERATORS = ['equal', 'not equal'];

// How a send action picks its OutboundMessage.sendTime.
type SendMode = 'delay' | 'timeOfDay';
const DEFAULT_SUBSCRIPTION_OPTIONS = [
  {
    title: 'Tier Title',
    field: 'tier.title',
  },
  {
    title: 'Tier Initial Price',
    field: 'tier.initialSubscriptionPrice',
  },
  {
    title: 'Tier Subsequent Price',
    field: 'tier.subsequentSubscriptionPrice',
  },
  {
    title: 'Tier Subscription Duration',
    field: 'tier.subscriptionDuration',
  },
  {
    title: 'Tier Initial Wallet Price',
    field: 'tier.initialWalletPrice',
  },
  {
    title: 'Tier Subsequent Wallet Price',
    field: 'tier.subsequentWalletPrice',
  },
  {
    title: 'Tier Wallet Duration',
    field: 'tier.walletDuration',
  },
  {
    title: 'Tier Wallet Rollover Duration',
    field: 'tier.walletRolloverDurationAmount',
  },

  {
    title: 'Subscription Registration Code',
    field: 'subscription.registrationCode.code',
  },
  {
    title: 'Subscription Partner Referral Code',
    field: 'subscription.partnerReferralCode.code',
  },
  {
    title: 'Subscription Initial Price',
    field: 'subscription.initialSubscriptionPrice',
  },
  {
    title: 'Subscription Subsequent Price',
    field: 'subscription.subsequentSubscriptionPrice',
  },
  {
    title: 'Subscription Initial Wallet Price',
    field: 'subscription.initialWalletPrice',
  },
  {
    title: 'Subscription Subsequent Wallet Price',
    field: 'subscription.subsequentWalletPrice',
  },

  {
    title: 'Referral ID',
    field: 'referral.id',
  },
  {
    title: 'Referral Registration Code',
    field: 'referral.registrationCode.code',
  },
  {
    title: 'Referrer Full Name',
    field: 'referral.referrer.name',
  },
];

type ValueComparisonEntry = {
  resource: string;
  resourceField: string;
  operator: string;
  value: string | string[];
};

type FormTypes = {
  id: string | null;
  title: string;
  flowStageId: string | null;
  conditions: {
    type: string;
    field: string;
    operator: string;
    value: string | string[];
    resource?: string;
    resourceField?: string;
    valueComparisons?: ValueComparisonEntry[];
  };
  actions: {
    type: TriggerActionType;
    recipientType: string;
    fishIds: string[];
    userIds: string[];
    userField?: string;
    recipientInboxType?: string;
    userRoleIds: string[];
    subject: string;
    body: string;
    senderId: string;
    senderField?: string;
    senderInboxType?: string;
    sendDelayAmount: number;
    sendDelayUnit: string;
    // 'HH:mm' venue time; replaces the delay when set.
    sendAtTimeOfDay: string;
    tagsToAdd: string[];
    tagsToRemove: string[];
    openTableTags: boolean;
    html?: string;
    htmlEditorJson?: any;
  };
  active: boolean;
};

const TriggerConfigModal = ({
  selectedStageIndex,
  selectedTrigger,
  setSelectedTrigger,
  closeModal,
  setSearchParams,
}: {
  selectedStageIndex: number;
  selectedTrigger?: Trigger;
  setSelectedTrigger: (trigger: Trigger) => void;
  closeModal: () => void;
  setSearchParams: (params: { id: string }) => void;
}) => {
  const [currentForm, setCurrentForm] = useState<any>(null);
  const [currentQuestions, setCurrentQuestions] = useState<any[]>([]);
  const [showVerifyDelete, setShowVerifyDelete] = useState<boolean>(false);
  const [filteredMergeTags, setFilteredMergeTags] = useState<MergeTag[]>([]);
  const [editorMergeTags, setEditorMergeTags] = useState<
    Record<string, { name: string; value: string; sample: string }>
  >({});
  const { user, hasPermission } = useAuth();
  // The modal doubles as a read-only trigger viewer for users who can open the
  // flow builder but lack FLOW_BUILDER_WRITE; every mutating control is gated.
  const canWrite = hasPermission(['FLOW_BUILDER_WRITE']);
  const { readAllTags, tagArray } = useTagAndSegment();
  const emailEditorRef = useRef<EditorRef>(null);
  const formRef = useRef(null);

  const {
    flowDetails,
    setFlowDetails,
    setAllFlows,
    allTypeformForms,
    allCustomForms,
    currentFlowStages,
    setCurrentFlowStages,
    flowStageTypes,
  } = useFlow();

  const legacySenderInboxType =
    LEGACY_SENDER_FIELD_INBOX_TYPES[selectedTrigger?.actions.senderField ?? ''];

  const triggerObj = useForm<FormTypes>({
    defaultValues: {
      id: selectedTrigger?.id || null,
      title: selectedTrigger?.title,
      flowStageId:
        selectedTrigger?.flowStageId ||
        currentFlowStages[selectedStageIndex]?.id ||
        null,
      conditions: {
        type: selectedTrigger?.conditions.type || null,
        field: selectedTrigger?.conditions.field || null,
        operator: selectedTrigger?.conditions.operator || null,
        value: selectedTrigger?.conditions.value || null,
        resource: selectedTrigger?.conditions.resource || null,
        resourceField: selectedTrigger?.conditions.resourceField || null,
        valueComparisons: selectedTrigger?.conditions.valueComparisons?.length
          ? selectedTrigger.conditions.valueComparisons
          : selectedTrigger?.conditions.resource
            ? [{
                resource: selectedTrigger.conditions.resource,
                resourceField: selectedTrigger.conditions.resourceField,
                operator: selectedTrigger.conditions.operator,
                value: selectedTrigger.conditions.value,
              }]
            : [{ resource: '', resourceField: '', operator: '', value: [] }],
      },
      actions: {
        type: selectedTrigger?.actions.type || 'EMAIL',
        recipientType: selectedTrigger?.actions.recipientType || 'fish',
        fishIds: selectedTrigger?.actions.fishIds || [],
        userIds: selectedTrigger?.actions.userIds || [],
        userField: selectedTrigger?.actions.userField || undefined,
        recipientInboxType: selectedTrigger?.actions.recipientInboxType || undefined,
        userRoleIds: selectedTrigger?.actions.userRoleIds || [],
        subject: selectedTrigger?.actions.subject || '',
        body: selectedTrigger?.actions.body || '',
        senderId: selectedTrigger?.actions.senderId || undefined,
        // A trigger saved with a legacy sender field shows, and saves, as its type.
        senderField: legacySenderInboxType
          ? undefined
          : selectedTrigger?.actions.senderField || undefined,
        senderInboxType: selectedTrigger?.actions.senderInboxType || legacySenderInboxType,
        sendDelayAmount: selectedTrigger?.actions.sendDelayAmount || 0,
        sendDelayUnit: selectedTrigger?.actions.sendDelayUnit || 'Minutes',
        sendAtTimeOfDay: selectedTrigger?.actions.sendAtTimeOfDay || '',
        tagsToAdd: selectedTrigger?.actions.tagsToAdd || [],
        tagsToRemove: selectedTrigger?.actions.tagsToRemove || [],
        openTableTags: selectedTrigger?.actions.openTableTags || false,
        html: selectedTrigger?.actions.html || '',
        htmlEditorJson:
          selectedTrigger?.actions.htmlEditorJson || EMPTY_EDITOR_DESIGN,
      },
      active: true,
    },
  });

  const {
    mergeTags,
    fetchMergeTags,
    showHTMLEditor,
    setShowHTMLEditor,
    isHtmlDesignBlank,
  } = useMessages();

  const { register, watch, setValue, getValues, handleSubmit, control } = triggerObj;

  const { fields: valueComparisonFields, append: appendComparison, remove: removeComparison } = useFieldArray({
    control,
    name: 'conditions.valueComparisons',
  });

  const conditionType = watch('conditions.type');

  const initialSendMode = (): SendMode => {
    if (selectedTrigger?.actions.sendAtTimeOfDay) return 'timeOfDay';
    return 'delay';
  };
  const [sendMode, setSendMode] = useState<SendMode>(initialSendMode);

  // Only the chosen mode's fields are saved, so the API never sees both.
  const chooseSendMode = (mode: SendMode) => {
    setSendMode(mode);
    if (mode === 'delay') setValue('actions.sendAtTimeOfDay', '');
    if (mode === 'timeOfDay') setValue('actions.sendDelayAmount', 0);
  };

  const timeOfDayError = triggerObj.formState.errors.actions?.sendAtTimeOfDay?.message;

  const exportEmail = async () => {
    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;

    const data: any = await new Promise((resolve) => {
      unlayer.exportHtml((d) => resolve(d));
    });

    if (isHtmlDesignBlank(data.design)) {
      setValue('actions.html', null);
      setValue('actions.htmlEditorJson', null);

      return { html: null, design: null };
    } else {
      setValue('actions.html', data.html);
      setValue('actions.htmlEditorJson', data.design);

      return { html: data.html, design: data.design };
    }
  };

  const onReady = (unlayer: any) => {
    const design = getValues('actions.htmlEditorJson');
    if (design) {
      unlayer.loadDesign(design);
    }
  };

  const extractQuestions = (depType: string, form: any): any[] => {
    const questionArray = [];
    if (depType === 'typeform') {
      const typeformQuestions = form?.fieldsJson.map((question: any) => {
        if (question.properties.fields) {
          return question.properties.fields.map((nestedQuestion: any) => ({
            title: nestedQuestion.title,
            field: nestedQuestion.id,
          }));
        }
        return {
          title: question.title,
          field: question.id,
        };
      });

      // filter out duplicate merge tags (first and last name already included from fish)
      if (!typeformQuestions) return questionArray;
      const filteredQuestions = typeformQuestions
        .flat()
        .filter((question: any) => {
          const questionTitle = question.title.toLowerCase();
          const include =
            questionTitle.includes('first name') ||
            questionTitle.includes('last name') ||
            questionTitle.includes('phone number') ||
            questionTitle.includes('email');
          return !include;
        });

      questionArray.push(...filteredQuestions);
    } else if (depType === 'customForm') {
      const customFormQuestions = Object.keys(
        form.formSchemaJson.properties,
      ).map((key) => ({
        title: form.formSchemaJson.properties[key]?.title,
        field: key,
      }));
      questionArray.push(...customFormQuestions);
    }
    return questionArray;
  };

  const onSubmit = async (data: FormTypes) => {
    let newTrigger: Trigger;

    const newData = data;
    if (selectedTrigger?.id) {
      const res = await service.updateTrigger(newData);
      if (!res.ok) {
        toastError('Error updating Trigger');
        return;
      }
      toastSuccess('Updated Trigger');
      newTrigger = await res.json();

      setCurrentFlowStages((prev: FlowStage[]) => [
        ...prev.slice(0, selectedStageIndex),
        {
          ...prev[selectedStageIndex!],
          flowStageTriggers: [
            ...prev[selectedStageIndex!].flowStageTriggers.map(
              (trigger: Trigger) => {
                if (trigger.id !== newTrigger.id) return trigger;
                return newTrigger;
              },
            ),
          ],
        },
        ...prev.slice(selectedStageIndex! + 1),
      ]);
    } else {
      let data: Flow;

      // ensure stage exists in db before attaching trigger
      if (!currentFlowStages[selectedStageIndex].createdAt) {
        const payload = {
          ...flowDetails,
          flowStages: [...currentFlowStages],
          userId: user.id,
        };

        // create the flow if it hasn't been created already
        if (!flowDetails.id) {
          const res = await service.createFlow(payload);
          if (!res.ok) {
            toastError('Error Creating Flow');
            return;
          }
          toastSuccess('Created Flow');
          data = await res.json();
          setAllFlows((prevFlows: FlowStage[]) => [...prevFlows, data]);
          setFlowDetails(data);
          setSearchParams({ id: data.id });
          // if the flow already exists, but the stage doesn't, updating the flow will create the stages
        } else {
          const res = await service.updateFlow(payload);
          if (!res.ok) {
            toastError('Error Updating Flow');
            return;
          }
          toastSuccess('Updated Flow');
          data = await res.json();
          setAllFlows((prevFlows: FlowStage[]) =>
            prevFlows.map((flow) => (flow.id !== data.id ? flow : data)),
          );
        }
      }

      const orderedStages = orderFlowStages(
        data?.flowStages || [...currentFlowStages],
      );
      if (data) {
        // Adopt the saved stages now so a failed trigger save retries against
        // them instead of re-creating the stage.
        setCurrentFlowStages(orderedStages);
      }

      // Stage ids are generated by the API: a stage saved just above only now has
      // its real id, while the builder's id for an unsaved stage is a placeholder.
      const { id: _id, ...dataWithoutId } = newData;
      const res = await service.createTrigger({
        ...dataWithoutId,
        ...(data && { flowStageId: orderedStages[selectedStageIndex].id }),
      });
      if (!res.ok) {
        toastError('Error Creating Trigger');
        return;
      }
      toastSuccess('Created Trigger');
      newTrigger = await res.json();

      setCurrentFlowStages([
        ...orderedStages.slice(0, selectedStageIndex),
        {
          ...orderedStages[selectedStageIndex],
          flowStageTriggers: [
            ...orderedStages[selectedStageIndex].flowStageTriggers,
            newTrigger,
          ],
        },
        ...orderedStages.slice(selectedStageIndex + 1),
      ]);

      setSelectedTrigger(newTrigger);
      setValue('id', newTrigger.id);
    }

    closeModal();
  };

  const handleDeleteTrigger = async (triggerId: string) => {
    const deleteRes = await service.deleteTriggers([triggerId], user.id);
    if (deleteRes.ok) {
      toastSuccess('Trigger Deleted');
    } else {
      toastError('Error Deleting Trigger');
    }
    const deletedId = await deleteRes.json();

    if (!deletedId) {
      console.error('Failted to delete Trigger', triggerId);
    }

    setCurrentFlowStages((prev: FlowStage[]) => [
      ...prev.slice(0, selectedStageIndex),
      {
        ...prev[selectedStageIndex],
        flowStageTriggers: [
          ...prev[selectedStageIndex].flowStageTriggers.filter(
            (trigger: Trigger) => {
              return trigger.id !== triggerId;
            },
          ),
        ],
      },
      ...prev.slice(selectedStageIndex + 1),
    ]);
  };

  const handleSaveHTMLSubmit = async () => {
    if (showHTMLEditor) {
      await exportEmail();
    }

    if (!triggerObj.getValues('actions.body')) {
      triggerObj.setValue('actions.body', 'html');
    }

    formRef.current?.requestSubmit();
  };

  const handleReloadEditor = (selected) => {
    const unlayer = emailEditorRef?.current?.editor;
    if (!unlayer) return;
    unlayer.loadDesign(selected.htmlEditorJson);
  };

  useEffect(() => {
    const stage = currentFlowStages[selectedStageIndex];
    setValue('flowStageId', stage?.id);
    if (stage?.flowStageTypeId === REGISTRATION_COMPLETION_ID) {
      // REGISTRATION_COMPLETION
      setCurrentQuestions(DEFAULT_SUBSCRIPTION_OPTIONS);
      return;
    }
    const lastDepId = stage?.dependencyIdentifiers?.at(-1);
    const lastDepType = flowStageTypes
      .find((type: FlowStageType) => type.id === stage?.flowStageTypeId)
      ?.dependencies.at(-1);

    if (!lastDepId || !lastDepType) return;

    type TypeformForm = {
      fieldsJson: any[];
      formId: string;
      formTitle: string;
      id: string;
      numberOfResponses: number;
    };

    type CustomForm = {
      formSchemaJson: any;
      formTitle: string;
      id: string;
      numberOfResponses: number;
    };

    const tempForm =
      lastDepType === 'typeform'
        ? allTypeformForms.find((f: TypeformForm) => f.formId === lastDepId)
        : allCustomForms.find((f: CustomForm) => f.id === lastDepId);

    setCurrentForm(tempForm);
    setCurrentQuestions(extractQuestions(lastDepType, tempForm));
  }, [selectedStageIndex, currentFlowStages]);

  useEffect(() => {
    const allMergeTags = [...mergeTags];

    // Generate merge tags from form questions
    const stage = currentFlowStages[selectedStageIndex] || null;
    if (
      currentQuestions.length &&
      stage?.flowStageTypeId !== REGISTRATION_COMPLETION_ID
    ) {
      const lastDepType = stage?.flowStageTypes?.dependencies.at(-1);
      for (const question of currentQuestions) {
        allMergeTags.push({
          id: `${lastDepType}-${question.field}-${question.title}`,
          label: question.title,
          type: lastDepType,
        });
      }
    }

    setFilteredMergeTags(allMergeTags);

    const editorTagObj: Record<
      string,
      {
        name: string;
        value: string;
        sample: string;
      }
    > = {};

    allMergeTags.forEach((mergeTag) => {
      const name = mergeTag.label.replaceAll(' ', '_').toLowerCase();

      editorTagObj[name] = {
        name: mergeTag.label,
        value: `{{${mergeTag.id}}}`,
        sample: 'Answer',
      };
    });

    setEditorMergeTags(editorTagObj);
  }, [mergeTags, currentQuestions, currentFlowStages]);

  useEffect(() => {
    const stageTypeId = currentFlowStages[selectedStageIndex]?.flowStageTypeId;
    if (stageTypeId) {
      fetchMergeTags(stageTypeId);
    }

    readAllTags();
  }, []);

  return (
    <FormProvider {...triggerObj}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className="items-between flex min-w-[600px] flex-col gap-4 py-2"
      >
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-bold">Configure Trigger</h2>

          <div className="flex gap-6">
            <div className="flex gap-2 rounded-md border px-2 py-1 text-sm">
              <h3 className="text-zinc-500">Flow:</h3>
              <h3>{flowDetails.flowTitle}</h3>
            </div>

            <div className="flex gap-2 rounded-md border px-2 py-1 text-sm">
              <h3 className="text-zinc-500">Stage:</h3>
              <h3>{currentFlowStages[selectedStageIndex]?.title}</h3>
            </div>

            <div className="flex gap-2 rounded-md border px-2 py-1 text-sm">
              <h3 className="text-zinc-500">Form:</h3>
              <h3>{currentForm?.title || currentForm?.formTitle}</h3>
            </div>
          </div>

          <div className="flex flex-col">
            <h3 className="font-bold">Title</h3>
            <div className="flex flex-col gap-2 rounded-md border p-2">
              <input
                type="text"
                placeholder="Enter a title..."
                className="w-full rounded-md border px-3 py-2"
                {...register('title')}
              />
            </div>
          </div>

          <div className="flex flex-col">
            <h3 className="font-bold">Condition</h3>
            <div className="flex flex-col gap-2 rounded-md border p-2">
              {/* Type Select */}
              <div className="flex flex-col">
                <label className="text-sm text-zinc-500">Type:</label>
                <Controller
                  name="conditions.type"
                  control={triggerObj.control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      value={value ? { value, label: value } : null}
                      onChange={(selected) => onChange(selected?.value || '')}
                      options={DEFAULT_TRIGGER_TYPES.map((type) => ({
                        value: type,
                        label: type,
                      }))}
                      placeholder="-- Select a Type --"
                      classNamePrefix="select"
                    />
                  )}
                />
              </div>

              {/* Comparison Details */}
              {conditionType === 'Comparison' && (
                <>
                  <div className="flex flex-col">
                    <label className="text-sm text-zinc-500">Question:</label>
                    <Controller
                      name="conditions.field"
                      control={triggerObj.control}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          value={
                            value
                              ? {
                                  value,
                                  label:
                                    currentQuestions.find(
                                      (q) => q.field === value,
                                    )?.title || value,
                                }
                              : null
                          }
                          onChange={(selected) =>
                            onChange(selected?.value || '')
                          }
                          options={currentQuestions.map((q) => ({
                            value: q.field,
                            label: q.title,
                          }))}
                          placeholder="-- Select a Question --"
                          classNamePrefix="select"
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-zinc-500">Answer is:</label>
                    <Controller
                      name="conditions.operator"
                      control={triggerObj.control}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          value={value ? { value, label: value } : null}
                          onChange={(selected) =>
                            onChange(selected?.value || '')
                          }
                          options={DEFAULT_OPERATORS.map((op) => ({
                            value: op,
                            label: op,
                          }))}
                          placeholder="-- Select an Operator --"
                          classNamePrefix="select"
                        />
                      )}
                    />
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm text-zinc-500">to:</label>
                    <input
                      type="text"
                      placeholder="Enter a value..."
                      className="mb-4 w-full rounded-md border px-3 py-2"
                      {...register('conditions.value')}
                    />
                  </div>
                </>
              )}

              {/* ValueComparison Details */}
              {conditionType === 'ValueComparison' && (
                <div className="flex flex-col gap-3">
                  {valueComparisonFields.map((field, index) => (
                    <ValueComparisonCondition
                      key={field.id}
                      control={triggerObj.control}
                      index={index}
                      flowStageType={
                        currentFlowStages[selectedStageIndex]?.flowStageTypes
                      }
                      showRemove={valueComparisonFields.length > 1}
                      onRemove={() => removeComparison(index)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => appendComparison({ resource: '', resourceField: '', operator: '', value: [] })}
                    className="w-fit rounded-md border border-dashed border-gray-400 px-3 py-1 text-sm text-gray-600 hover:border-blue-500 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!canWrite}
                  >
                    + Add Condition
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions Section */}
          <div className="flex flex-col gap-2">
            <h3 className="font-bold">Actions</h3>

            {/* Tags */}
            <div>
              <h3 className="text-zinc-500">Tags</h3>
              <div className="flex flex-col gap-4 rounded-md border p-4">
                {/* Tags to Add */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-zinc-700">
                    Tags to Add:
                  </label>
                  <Controller
                    name="actions.tagsToAdd"
                    control={triggerObj.control}
                    render={({ field: { value = [], onChange } }) => {
                      const selectedTagObjects = tagArray.filter((tag: Tag) =>
                        value.includes(tag.id),
                      );
                      const filteredTagOptions = tagArray.filter(
                        (tag: Tag) => !value.includes(tag.id),
                      );

                      return (
                        <Select
                          isMulti
                          value={selectedTagObjects.map((tag: Tag) => ({
                            value: tag?.id,
                            label: tag?.name,
                          }))}
                          onChange={(selected) =>
                            onChange(selected.map((opt) => opt.value))
                          }
                          options={filteredTagOptions.map((tag: Tag) => ({
                            value: tag?.id,
                            label: tag?.name,
                          }))}
                          placeholder="Select or enter tags to add..."
                          classNamePrefix="select"
                          isClearable
                          isSearchable={false}
                        />
                      );
                    }}
                  />
                </div>

                {/* Tags to Remove */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-zinc-700">
                    Tags to Remove:
                  </label>
                  <Controller
                    name="actions.tagsToRemove"
                    control={triggerObj.control}
                    render={({ field: { value = [], onChange } }) => {
                      const selectedTagObjects = tagArray.filter((tag: Tag) =>
                        value.includes(tag.id),
                      );
                      const filteredTagOptions = tagArray.filter(
                        (tag: Tag) => !value.includes(tag.id),
                      );

                      return (
                        <Select
                          isMulti
                          value={selectedTagObjects.map((tag: Tag) => ({
                            value: tag?.id,
                            label: tag?.name,
                          }))}
                          onChange={(selected) =>
                            onChange(selected.map((opt) => opt.value))
                          }
                          options={filteredTagOptions.map((tag: Tag) => ({
                            value: tag?.id,
                            label: tag?.name,
                          }))}
                          placeholder="Select or enter tags to remove..."
                          classNamePrefix="select"
                          isClearable
                          isSearchable={false}
                        />
                      );
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Controller
                    name={'actions.openTableTags'}
                    control={triggerObj.control}
                    render={({ field: { onChange } }) => {
                      const openTableTags =
                        triggerObj.getValues()?.actions?.openTableTags;

                      return (
                        <div>
                          <label
                            className="text-sm font-medium text-zinc-700"
                            htmlFor={'actions.openTableTags'}
                          >
                            Send Tags to OpenTable?:
                          </label>
                          <input
                            className={'m-2'}
                            checked={openTableTags ? true : false}
                            type={'checkbox'}
                            onChange={(selected) => {
                              onChange(selected?.target.checked || false);
                            }}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div>
              <div className="mt-4 mb-2 flex items-center gap-4">
                <div className="flex w-fit justify-center">
                  <span className="text-zinc-500">Message</span>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-md border p-2">
                <TriggerOutboundMessageForm
                  filteredMergeTags={filteredMergeTags}
                  setShowHTMLEditor={setShowHTMLEditor}
                  showHTMLEditor={showHTMLEditor}
                  emailEditorRef={emailEditorRef}
                  handleReloadEditor={handleReloadEditor}
                  exportEmail={exportEmail}
                  stageTypeId={
                    currentFlowStages[selectedStageIndex]?.flowStageTypeId
                  }
                />

                <div
                  className={`border-1 ${showHTMLEditor ? 'block' : 'hidden'}`}
                >
                  <EmailEditor
                    key={JSON.stringify(Object.keys(editorMergeTags))}
                    ref={emailEditorRef}
                    onReady={onReady}
                    options={{
                      displayMode: 'email',
                      appearance: {
                        panels: {
                          tools: {
                            dock: 'left',
                          },
                        },
                      },
                      mergeTags: editorMergeTags,
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm text-zinc-500">Send:</label>
                  <div className="flex items-center gap-4 text-sm">
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="sendMode"
                        checked={sendMode === 'delay'}
                        onChange={() => chooseSendMode('delay')}
                      />
                      After a delay
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="sendMode"
                        checked={sendMode === 'timeOfDay'}
                        onChange={() => chooseSendMode('timeOfDay')}
                      />
                      At a time of day
                    </label>
                  </div>

                  {sendMode === 'delay' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Enter a number..."
                        className="w-fit rounded-md border px-2 py-1"
                        onKeyDown={(e) => {
                          if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
                            e.preventDefault();
                          }
                        }}
                        {...register('actions.sendDelayAmount')}
                      />

                      <label className="text-sm text-zinc-500">X</label>

                      <Controller
                        name="actions.sendDelayUnit"
                        control={triggerObj.control}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            value={value ? { value, label: value } : null}
                            onChange={(selected) =>
                              onChange(selected?.value || '')
                            }
                            options={DEFAULT_DELAY_UNITS.map((unit) => ({
                              value: unit,
                              label: unit,
                            }))}
                            menuPlacement="top"
                            placeholder="-- Select a Unit --"
                            classNamePrefix="select"
                          />
                        )}
                      />
                    </div>
                  )}

                  {sendMode === 'timeOfDay' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        className="w-fit rounded-md border px-2 py-1"
                        aria-label="Send at time of day"
                        {...register('actions.sendAtTimeOfDay', {
                          validate: (v) => sendMode !== 'timeOfDay' || !!v || 'Pick a time of day',
                        })}
                      />
                      <span className="text-xs text-zinc-500">
                        Eastern time. The next one after the stage fires: today, or tomorrow if
                        it has passed.
                      </span>
                    </div>
                  )}
                  {timeOfDayError && (
                    <p className="text-sm text-red-500">{timeOfDayError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full justify-between">
          {showHTMLEditor ? (
            <Button
              variant="primary"
              type="button"
              onClick={handleSaveHTMLSubmit}
              disabled={!canWrite}
            >
              {selectedTrigger?.id ? 'Update' : 'Submit'}
            </Button>
          ) : (
            <Button variant="primary" type="submit" disabled={!canWrite}>
              {selectedTrigger?.id ? 'Update' : 'Submit'}
            </Button>
          )}

          {selectedTrigger?.id && !showVerifyDelete && (
            <Button
              variant="destructive"
              type="button"
              onClick={() => {
                setShowVerifyDelete(true);
              }}
              disabled={!canWrite}
            >
              Delete
            </Button>
          )}

          {showVerifyDelete && selectedTrigger && (
            <div className="flex gap-4">
              <Button
                variant="destructive"
                type="button"
                onClick={() => {
                  setShowVerifyDelete(false);
                  handleDeleteTrigger(selectedTrigger?.id);
                  closeModal();
                }}
                disabled={!canWrite}
              >
                Confirm Delete
              </Button>

              <Button
                variant="outline"
                type="button"
                onClick={() => {
                  setShowVerifyDelete(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default TriggerConfigModal;
